/**
 * Analytics configuration — reads the public tracking IDs the marketing /
 * growth stack needs. All IDs are `NEXT_PUBLIC_*` (shipped to the browser by
 * design) and gated on presence + format: when an ID is unset or malformed,
 * that platform's loader and event calls become no-ops, so the site runs fine
 * in dev / previews without any tracking wired.
 *
 * Read via functions (not module-level constants) so tests can set
 * `process.env.*` before calling. Next still inlines the `process.env.*`
 * literals at build time inside these functions.
 *
 * The format check is belt-and-braces: these IDs get template-interpolated
 * into inline `<Script>` tags (see analytics-scripts.tsx), so validating them
 * first keeps a misconfigured / attacker-controlled env value out of a JS
 * source position, and makes a malformed ID fail closed instead of loading the
 * wrong tag. (Pattern adopted from ~/developer/dictionary-builder.)
 *
 * These are marketing IDs (GA4 / Facebook Pixel / Microsoft Clarity), NOT
 * AI/STT providers — so they're exempt from the "never leak the tech stack"
 * rule (see no-leak guard in batch-transcription-page.test.tsx).
 */

// GA4 measurement IDs look like `G-XXXXXX…`; this also rejects legacy
// Universal Analytics (`UA-…`) and Tag Manager (`GTM-…`) IDs so a stale env
// fails closed rather than loading the wrong tag.
const GA4_ID_PATTERN = /^G-[A-Z0-9]{6,}$/
// Facebook Pixel IDs are numeric (15–16 digits in practice; allow a range).
const FB_PIXEL_ID_PATTERN = /^\d{6,20}$/
// Microsoft Clarity project IDs are short lowercase alphanumeric tokens.
const CLARITY_ID_PATTERN = /^[a-z0-9]{4,20}$/

const validated = (raw: string | undefined, pattern: RegExp): string => {
  const value = raw?.trim() ?? ''
  return value && pattern.test(value) ? value : ''
}

/** GA4 Measurement ID, e.g. `G-XXXXXXXXXX`. Empty string when unset/malformed. */
export const gaMeasurementId = (): string =>
  validated(process.env.NEXT_PUBLIC_GTAG_ID, GA4_ID_PATTERN)

/** Facebook Pixel ID, e.g. `786964061836797`. Empty string when unset/malformed. */
export const fbPixelId = (): string =>
  validated(process.env.NEXT_PUBLIC_FB_PIXEL_ID, FB_PIXEL_ID_PATTERN)

/** Microsoft Clarity project ID, e.g. `7mykmpeygy`. Empty string when unset/malformed. */
export const clarityProjectId = (): string =>
  validated(process.env.NEXT_PUBLIC_CLARITY_ID, CLARITY_ID_PATTERN)

export const isGaEnabled = (): boolean => gaMeasurementId().length > 0
export const isPixelEnabled = (): boolean => fbPixelId().length > 0
export const isClarityEnabled = (): boolean => clarityProjectId().length > 0
